import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Button, Input, ListScreen, Select} from '../components';
import {
    fetchAiFunctions,
    cleanAiFunctions,
    setUser,
    changeUserSetting,
    deleteAiFunction,
} from '../actions';
import {__, formatAmount, formatCurrency, formatDate, getCurrency, request, toNumber} from '../functions';
import '../assets/styles/ai-functions.css';
import DeleteIcon from '@mui/icons-material/Delete';
import {AI_FUNCTION_NAMES, AI_FUNCTION_DESCRIPTIONS, AI_FUNCTION_VARIABLE_NAMES} from '../config';
import EditIcon from "@mui/icons-material/Edit";
import {IconButton, Tooltip} from "@mui/material";
import CopyIcon from "@mui/icons-material/FileCopy";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/AddCircle";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

class AiFunctionsScreen extends ListScreen {
    title = __('AI funkcie');

    state = { ...ListScreen.state, ...{
        lightbox: {
            delete: false,
            run: false,
        },
    }};

    isValidPermission() {
        return this.hasPermission('aifunctions');
    }

    getTableName() {
        return 'ai-functions';
    }

    getFilter() {
        const { items } = this.props;

        return _.reduce(items.filter, (result, options, key) => {
            switch (key) {
                case 'name':
                    return { ...result, ...{ [key]: {
                        type: 'input',
                        name: __('Názov'),
                        value: '',
                    }}};

                default:
                    return result;
            }
        }, {})
    }

    getColumns() {
        return [
            <div>{__('Názov')}</div>,
            <div>{__('Popis')}</div>,
            <div>{__('Vytvorené')}</div>,
            <div></div>,
        ];
    }

    getCells(item) {
        let category = null;
        const isDefault = toNumber(item.def) === 1;

        if (isDefault) {
            category = <div className="category" style={{ backgroundColor: '#c8c4c4' }}>{__('System')}</div>;
        }

        const functionName = isDefault && _.has(AI_FUNCTION_NAMES, item.name) ? AI_FUNCTION_NAMES[item.name] : item.name;
        const functionDescription = isDefault && _.has(AI_FUNCTION_DESCRIPTIONS, item.settings.description) ? AI_FUNCTION_DESCRIPTIONS[item.settings.description] : item.settings.description;

        return [
            <div>
                <div><Tooltip title={functionName}><span>{_.truncate(functionName, { length: 48 })}</span></Tooltip></div>
                {category}
            </div>,
            <div>{functionDescription !== null && functionDescription !== '' ? <Tooltip title={functionDescription}><span>{_.truncate(functionDescription, { length: 64 })}</span></Tooltip> : '-'}</div>,
            <div>{formatDate(item.created, 'dd.mm.yyyy hh:ii')}</div>,
            <div>
                {this.hasPermission('aifunctions-run') ? this.renderTableButton(
                    item.id,
                    __('Spustiť'),
                    <SmartToyIcon color="orange" />,
                    (callbackLoading, callback) => this.showRun(item),
                    {}
                ) : null}
                {this.hasPermission('aifunctions-edit') ? this.renderTableButton(
                    item.id,
                    __('Upraviť'),
                    <EditIcon />,
                    (callbackLoading, callback) => this.redirect(`/ai-functions/view/${item.id}`),
                    {}
                ) : null}
                {this.hasPermission('aifunctions-delete') ? this.renderTableButton(
                    item.id,
                    __('Zmazať'),
                    <DeleteIcon />,
                    (callbackLoading, callback) => this.confirmDelete(item.id, callbackLoading, callback),
                    { disabled: isDefault }
                ) : null}
            </div>,
        ];
    }

    getTags() {
        const { items, user } = this.props;

        if (!this.hasPermission('aifunctions-create')) {
            return [];
        }

        return [
            {
                name: __('Nová funkcia'),
                onClick: () => this.redirect('/ai-functions/view/0'),
                disabled: this.isDemo() || toNumber(user.user_eshop_id) === 0,
                color: 'primary',
                icon: <AddIcon />,
            },
            {
                name: `${__('Aktuálny kredit')}: ${formatAmount(items.credit, getCurrency(), 4)}`,
                color: 'secondary',
                icon: <MonetizationOnIcon />,
            },
        ];
    }

    getEmptyText() {
        return __('Zatiaľ nemáte žiadne funkcie');
    }

    confirmDelete(id, callbackLoading, callback) {
        this.showLightbox('delete', { id, callbackLoading, callback });
    }

    async delete() {
        const { deleteAiFunction, items } = this.props;
        const { lightbox } = this.state;

        // Zavolame loading
        lightbox.delete.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('delete');

        // Zmazeme
        await deleteAiFunction(this, lightbox.delete.id);

        // Zavolame callback
        lightbox.delete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    copyOutput(output) {
        navigator.clipboard.writeText(output);

        this.showSnackbar('success', __('Skopirované do schránky'));
    }

    showRun(item) {
        const variables = _.reduce(item.default_variables, (result, variable_value, variable_name) => {
            if (_.isObject(variable_value)) {
                variable_value = variable_value.default;
            }

            return { ...result, [variable_name]: variable_value };
        }, {});

        this.showLightbox('run', { loading: false, type: 'max', item, variables, input: '', output: null, price: {}, totalPrice: 0 });
    }

    onChangeRunLoading(loading) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, loading } } });
    }

    onChangeRunInput(input) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, input, price: {}, totalPrice: 0, output: null } } });
    }

    onChangeRunVariable(name, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, variables: { ...lightbox.run.variables, [name]: value } } } });
    }

    onChangeRunType(type) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, type, price: {}, totalPrice: 0, output: null } } });
    }

    afterPrice(price, totalPrice) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, price, totalPrice } } });
    }

    afterRun(output, totalPrice) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, run: { ...lightbox.run, output, totalPrice } } });
    }

    getPrice() {
        const { lightbox } = this.state;

        const input = lightbox.run.input;
        let output = '';

        if (lightbox.run.type === 'same') {
            output = input;
        }

        this.onChangeRunLoading(true);

        request(`/ai-functions/getPrice/${lightbox.run.item.slug}`, { input, output }, 'POST').then(response => {
            const { status, data } = response.data;

            this.onChangeRunLoading(false);

            if (status === 'error' || !_.has(data, 'price')) {
                this.showSnackbar('error', __('Nepodarilo sa načítať cenu'));
                return;
            }

            this.afterPrice(data.price, data.total_price);
        });
    }

    run() {
        const { lightbox } = this.state;

        this.onChangeRunLoading(true);

        request(`/ai-functions/run/${lightbox.run.item.slug}`, {
            input: lightbox.run.input,
            item_type: 'manual',
            item_id: null,
            variables: lightbox.run.variables,
        }, 'POST').then(response => {
            const { status, data } = response.data;

            this.onChangeRunLoading(false);

            if (status === 'error') {
                let error = data.error;

                if (error === 'Empty credit') {
                    error = __('Nemáte dostatočný kredit');
                }

                this.showSnackbar('error', `${__('Chyba')}: ${error}`);
                return;
            }

            if (_.has(data, 'processing')) {
                this.showSnackbar('success', __('Funkcia sa spracováva, výstup bude dostupný o pár minút. Nájdete ho v sekcii AI úlohy.'));
                return;
            }

            this.afterRun(data.output, data.price);
        });
    }

    renderAfterHeader() {
        return (
            <div className="ai-functions-header">
                <div className="ai-functions-header__item">
                    <div className="ai-functions-header__item__name">{__('Vlastné AI funkcie')}</div>
                    <div className="ai-functions-header__item__text">{__('Vytvorte si AI funkcie ktoré sa budú riadiť vašimi inštrukciami.')}</div>
                </div>
                <div className="ai-functions-header__item">
                    <div className="ai-functions-header__item__name">{__('Prílohy pre AI')}</div>
                    <div className="ai-functions-header__item__text">{__('Môžete nahrať svoje prílohy s ktorými bude AI pracovať.')}</div>
                </div>
                <div className="ai-functions-header__item">
                    <div className="ai-functions-header__item__name">{__('Doplnky pre AI')}</div>
                    <div className="ai-functions-header__item__text">{__('Nastavte AI aktuálne vyhľadávanie výsledkov cez Google.')}</div>
                </div>
                <div className="ai-functions-header__item">
                    <div className="ai-functions-header__item__name">{__('Potrebujete pomôcť?')}</div>
                    <div className="ai-functions-header__item__text">{__('Kontaktujte nás, radi vám s nastavením AI pomôžeme.')}</div>
                </div>
            </div>
        );
    }

    renderScreen() {
        const { lightbox } = this.state;

        return (
            <div>
                {this.renderLightbox(
                    'run',
                    !_.isEmpty(lightbox.run) ? _.truncate((toNumber(lightbox.run.item.def) === 1 && _.has(AI_FUNCTION_NAMES, lightbox.run.item.name) ? AI_FUNCTION_NAMES[lightbox.run.item.name] : lightbox.run.item.name), { length: 48 }) : null,
                    !_.isEmpty(lightbox.run) ? <div className="run-ai-function">
                        <div className="run-ai-function__label input__label">{__('Popis')}</div>
                        <div className="run-ai-function__text">{toNumber(lightbox.run.item.def) === 1 && _.has(AI_FUNCTION_DESCRIPTIONS, lightbox.run.item.settings.description) ? AI_FUNCTION_DESCRIPTIONS[lightbox.run.item.settings.description] : lightbox.run.item.settings.description}</div>
                        <Input
                            label={__('Vstup')}
                            value={lightbox.run.input}
                            onChange={value => this.onChangeRunInput(value)}
                            multiline
                        />
                        {!_.isEmpty(lightbox.run.item.default_variables) ? _.map(lightbox.run.item.default_variables, (variable_value, variable_name) => {
                            const variable_text = _.has(AI_FUNCTION_VARIABLE_NAMES, variable_name)
                                ? AI_FUNCTION_VARIABLE_NAMES[variable_name]
                                : variable_name;

                            if (_.isObject(variable_value)) {
                                return <Select
                                    label={variable_text}
                                    options={variable_value.options}
                                    value={lightbox.run.variables[variable_name]}
                                    onChange={value => this.onChangeRunVariable(variable_name, value)}
                                    allowEmpty={false}
                                />;
                            }

                            return <Input
                                label={variable_text}
                                value={lightbox.run.variables[variable_name]}
                                onChange={value => this.onChangeRunVariable(variable_name, value)}
                            />;
                        }) : null}
                        <Select
                            label={__('Výpočet odhadovaného výstupu')}
                            options={{ max: __('Maximálny počet tokenov - ideálne pre väčšinu funkcií'), same: __('Rovnaký počet tokenov ako vstup - ideálne pre preklady') }}
                            value={lightbox.run.type}
                            onChange={value => this.onChangeRunType(value)}
                            allowEmpty={false}
                        />
                        {lightbox.run.output !== null ? <div className="run-ai-function__output"><Input
                            label={__('Výstup')}
                            value={lightbox.run.output}
                            multiline
                        /><Tooltip title={__('Skopírovať do schránky')}>
                            <IconButton
                                onClick={() => this.copyOutput(lightbox.run.output)}
                            >
                                <CopyIcon />
                            </IconButton>
                        </Tooltip></div> : null}
                        {lightbox.run.output !== null ? <div className="run-ai-function__prices">
                            <div className="run-ai-function__label input__label">{__('Výsledná cena')}</div>
                            <div className="run-ai-function__prices__final">{lightbox.run.totalPrice.toString().replace('.', ',')} {formatCurrency(getCurrency())}</div>
                            <div className="run-ai-function__prices__text">{__('Cena vám bola stiahnutá z kreditu.')}</div>
                        </div> : null}
                        {!_.isEmpty(lightbox.run.price) && lightbox.run.output === null ? <div className="run-ai-function__prices">
                            <div className="run-ai-function__label input__label">{__('Nacenenie')}</div>
                            <div className="run-ai-function__prices__price">
                                <span>Prompt:</span>
                                <span>{lightbox.run.price.input.prompt.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                            </div>
                            <div className="run-ai-function__prices__price">
                                <span>{__('Inštrukcie')}:</span>
                                <span>{lightbox.run.price.input.instructions.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                            </div>
                            {_.map(lightbox.run.price.input.files, (filePrice, fileName) => {
                                return (
                                    <div className="run-ai-function__prices__price">
                                        <span>{`${__('Súbor')} - ${fileName}`}:</span>
                                        <span>{filePrice.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                                    </div>
                                );
                            })}
                            <div className="run-ai-function__prices__price">
                                <span>{__('Vstup')}:</span>
                                <span>{lightbox.run.price.input.input.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                            </div>
                            {_.has(lightbox.run.price.input, 'addon_search') ? <div className="run-ai-function__prices__price">
                                <span>{__('Google vyhľadávanie')}:</span>
                                <span>{lightbox.run.price.input.addon_search.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                            </div> : null}
                            <div className="run-ai-function__prices__price">
                                <span>{__('Výstup')}:</span>
                                <span>{lightbox.run.price.output.output.toString().replace('.', ',')} {formatCurrency(getCurrency())}</span>
                            </div>
                            <div className="run-ai-function__prices__text">{__('Výsledná cena je orientačná. Finálnu cenu ovplyvňuje velkosť výstupu ktorý dopredu nevieme odhadnúť. Orientačná cena počíta s maximálnym počtom znakov vo výstupe. Finálna cena by mala byť nižšia ako orientačná.')}</div>
                        </div> : null}
                        {_.isEmpty(lightbox.run.price)
                            ? <Button
                                onClick={() => this.getPrice()}
                                loading={lightbox.run.loading}
                            >{__('Prepočítať')}</Button>
                            : <Button
                                onClick={() => this.run()}
                                color="green"
                                loading={lightbox.run.loading}
                            >{lightbox.run.output !== null ? __('Spustiť znova') : `${__('Spustiť za')} ${lightbox.run.totalPrice.toString().replace('.', ',')} ${formatCurrency(getCurrency())}`}</Button>}
                    </div> : null,
                    '',
                    __('Zrušiť'),
                )}
                {this.renderLightbox(
                    'delete',
                    __('Naozaj chcete zmazať túto funkciu?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.delete()
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ ai_functions, user }) => ({ items: ai_functions, user });

export default withCookies(connect(stateToProps, {
    fetch: fetchAiFunctions,
    clean: cleanAiFunctions,
    setUser,
    changeUserSetting,
    deleteAiFunction,
})(AiFunctionsScreen));
